import * as React from 'react';
import {
  Search,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import type { RoleListItem } from '@/types/role';

interface RoleSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: RoleListItem[];
  value?: number;
  onSelect: (roleId: number | undefined) => void;
  loading?: boolean;
  required?: boolean;
}

const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export function RoleSearchModal({
  open,
  onOpenChange,
  roles,
  value,
  onSelect,
  loading = false,
  required = false,
}: RoleSearchModalProps) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setPage(1);
    }
  }, [open]);

  // Filter roles based on search
  const filteredRoles = React.useMemo(() => {
    if (!debouncedSearch) return roles;

    const query = debouncedSearch.toLowerCase();
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(query) ||
        (role.description && role.description.toLowerCase().includes(query))
    );
  }, [roles, debouncedSearch]);

  // Paginate roles
  const paginatedRoles = React.useMemo(() => {
    return filteredRoles.slice(0, page * ITEMS_PER_PAGE);
  }, [filteredRoles, page]);

  const hasMore = paginatedRoles.length < filteredRoles.length;

  // Handle role selection
  const handleSelect = React.useCallback(
    (roleId: number | undefined) => {
      onSelect(roleId);
      onOpenChange(false);
    },
    [onSelect, onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Select Role</DialogTitle>
        </DialogHeader>

        {/* Search Input */}
        <div className="px-6 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <Separator />

        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="max-h-[50vh] px-6 py-4">
            {loading ? (
              <div className="py-12 text-center text-muted-foreground">
                <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin opacity-50" />
                <p className="text-sm">Loading roles...</p>
              </div>
            ) : (
              <>
                {/* No role option */}
                {!required && (
                  <button
                    onClick={() => handleSelect(undefined)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent transition-colors text-left',
                      !value && 'bg-accent'
                    )}
                  >
                    <span className="text-muted-foreground">No role selected</span>
                  </button>
                )}

                {/* Role items */}
                {paginatedRoles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleSelect(role.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent transition-colors text-left',
                      value === role.id && 'bg-accent'
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate">{role.name}</span>
                      </div>
                      {role.description && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {role.description}
                        </p>
                      )}
                    </div>
                  </button>
                ))}

                {/* Load more */}
                {hasMore && (
                  <Button
                    variant="ghost"
                    className="w-full mt-2"
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Load More ({filteredRoles.length - paginatedRoles.length} remaining)
                  </Button>
                )}

                {/* No Results Message */}
                {filteredRoles.length === 0 && (
                  <div className="py-12 text-center text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">No roles found</p>
                    {debouncedSearch && (
                      <p className="text-xs mt-1">Try a different search term</p>
                    )}
                  </div>
                )}
              </>
            )}
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical">
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
